/**
 * FormsList — /crm/settings/forms. Every form in the workspace with status, version,
 * and response counts; admins create new forms or import the old WCR site's
 * questionnaires losslessly (one-click when the WCR DB is reachable, paste
 * JSON otherwise).
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Archive, Check, ClipboardList, Copy, Download, Loader2, MoreVertical, Plus, Trash2, X } from 'lucide-react';
import api from '../../../services/api';
import { CrmForm } from '../../types';
import { relTime, useCanEditForms, usePersistedState, FORM_SURFACES, formSurface } from '../../lib';
import { FilterBar, PageHeader, SearchInput, SegmentedTabs } from '../../components';
import type { SegTab } from '../../components';
import { useConfirm, Z } from '../../components/ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

function statusPill(status: string): string {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'closed': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'archived': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    default: return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  }
}

// Filter chips ('' is the multi-select "All" tab: everything except archived,
// mirroring Studies).
type FormFilter = '' | 'active' | 'closed' | 'draft' | 'archived';
const FILTERS: { value: FormFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];
// Match one status value (drives per-tab counts). '' = All non-archived.
const matchesOne = (f: CrmForm, val: string) =>
  val === '' ? f.status !== 'archived' : f.status === val;
// Visible when nothing is selected (=> non-archived) or ANY selected value matches.
const matchesSet = (f: CrmForm, sel: string[]) =>
  sel.length === 0 ? f.status !== 'archived' : sel.some((v) => matchesOne(f, v));

// Statuses an admin can set by hand from the form's action menu.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

interface WcrQuestionnaire {
  id: string;
  name: string;
  study_slug: string | null;
  study_title: string | null;
  question_count: number;
}

export default function FormsList() {
  const navigate = useNavigate();
  const canEdit = useCanEditForms();
  const confirm = useConfirm();
  const [forms, setForms] = useState<CrmForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Multi-select status filter (empty = All non-archived). Key bumped to v2:
  // the old pref held a scalar FormFilter and would break the array checks.
  const [filters, setFilters] = usePersistedState<string[]>('forms.filterv2', ['active']);
  const [search, setSearch] = useState(''); // client-side filter over already-loaded forms (name/title)
  const [menuFor, setMenuFor] = useState<string | null>(null); // form id with its action menu open
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null); // fixed-position anchor for the open menu
  const [acting, setActing] = useState<string | null>(null);   // form id being archived/deleted

  const closeMenu = () => { setMenuFor(null); setMenuPos(null); };

  // The action menu is rendered through a portal with fixed positioning so it
  // escapes the card's `overflow-hidden` (which clips an absolutely-positioned
  // menu on the last row of a group). Anchor it to the button and flip it up
  // when there isn't room below.
  const openMenu = (id: string, btn: HTMLElement) => {
    if (menuFor === id) { closeMenu(); return; }
    const rect = btn.getBoundingClientRect();
    const MENU_W = 176; // w-44
    const MENU_H = 280; // approx full menu height
    const openUp = rect.bottom + MENU_H + 8 > window.innerHeight;
    setMenuPos({
      top: openUp ? Math.max(8, rect.top - MENU_H - 4) : rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_W),
    });
    setMenuFor(id);
  };

  // A fixed menu detaches from its button on scroll/resize — close it instead.
  useEffect(() => {
    if (!menuFor) return;
    const close = () => closeMenu();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuFor]);

  // New form
  const [creating, setCreating] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSurface, setNewSurface] = useState('questionnaire'); // item #05 group

  // WCR import
  const [importOpen, setImportOpen] = useState(false);
  const [wcrLoading, setWcrLoading] = useState(false);
  const [wcrAvailable, setWcrAvailable] = useState<boolean | null>(null);
  const [wcrList, setWcrList] = useState<WcrQuestionnaire[]>([]);
  const [importing, setImporting] = useState<string | null>(null);
  const [pasteJson, setPasteJson] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/forms');
      setForms(res.data?.forms || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const title = newTitle.trim();
    if (!title || creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/forms', { title, surface: newSurface });
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setCreating(false);
    }
  };

  const openImport = async () => {
    setImportOpen(true);
    setNotice(null);
    if (wcrAvailable !== null) return;
    setWcrLoading(true);
    try {
      const res = await api.get('/api/crm/forms/wcr-questionnaires');
      setWcrAvailable(Boolean(res.data?.available));
      setWcrList(res.data?.questionnaires || []);
    } catch {
      setWcrAvailable(false);
    } finally {
      setWcrLoading(false);
    }
  };

  const importById = async (q: WcrQuestionnaire) => {
    if (importing) return;
    setImporting(q.id);
    setError(null);
    try {
      const res = await api.post('/api/crm/forms/import', { wcr_questionnaire_id: q.id });
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setImporting(null);
    }
  };

  const setStatus = async (f: CrmForm, status: string) => {
    closeMenu();
    if (f.status === status) return;
    setActing(f.id);
    setError(null);
    try {
      await api.patch(`/api/crm/forms/${f.id}`, { status });
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setActing(null);
    }
  };

  const deleteForm = async (f: CrmForm) => {
    closeMenu();
    if (!(await confirm({
      title: `Permanently delete "${f.title}"?`,
      message: (
        <>
          <p>This cannot be undone.</p>
          <p className="mt-2">(Forms with responses can&apos;t be deleted — archive them instead.)</p>
        </>
      ),
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    setActing(f.id);
    setError(null);
    try {
      await api.delete(`/api/crm/forms/${f.id}`);
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setActing(null);
    }
  };

  // Copy a form's questions + settings into a fresh draft so a new form can be
  // built from an existing one instead of starting from scratch.
  const duplicate = async (f: CrmForm) => {
    closeMenu();
    setActing(f.id);
    setError(null);
    try {
      const res = await api.post(`/api/crm/forms/${f.id}/duplicate`, {});
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setActing(null);
    }
  };

  const importPasted = async () => {
    if (importing) return;
    let parsed: any;
    try {
      parsed = JSON.parse(pasteJson);
    } catch {
      setError('That is not valid JSON — paste the questionnaire row exported from the WCR admin.');
      return;
    }
    setImporting('paste');
    setError(null);
    try {
      const res = await api.post('/api/crm/forms/import', { questionnaire: parsed });
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setImporting(null);
    }
  };

  const counts = Object.fromEntries(
    FILTERS.map((f) => [f.value, forms.filter((form) => matchesOne(form, f.value)).length]),
  ) as Record<string, number>;
  const statusTabs: SegTab[] = FILTERS.map((f) => ({ value: f.value, label: f.label, count: counts[f.value] }));
  // Client-side name/title search over the already-loaded forms (CrmForm exposes `title`).
  const q = search.trim().toLowerCase();
  const visibleForms = forms
    .filter((f) => matchesSet(f, filters))
    .filter((f) => !q || f.title.toLowerCase().includes(q));

  const renderRow = (f: CrmForm) => (
    <div
      key={f.id}
      className="relative flex items-center gap-3 px-4 py-3.5 transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:px-5"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/crm/settings/forms/${f.id}`)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/crm/settings/forms/${f.id}`); } }}
        className="min-w-0 flex-1 cursor-pointer text-left focus:outline-none"
      >
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{f.title}</span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusPill(f.status)}`}>
            {f.status}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-400">
          {f.version > 0 ? `v${f.version}` : 'never published'}
          {f.imported_questionnaire_id ? ' · imported' : ''}
          {` · updated ${relTime(f.updated_at)}`}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{f.counts?.completed ?? 0}</p>
        <p className="text-[11px] text-gray-400">completed</p>
      </div>
      {canEdit && (
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={(e) => openMenu(f.id, e.currentTarget)}
            disabled={acting === f.id}
            aria-label="Form actions"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuFor === f.id && menuPos && createPortal(
            <>
              <div className={`fixed inset-0 ${Z.overlay}`} onClick={closeMenu} />
              {/* Portaled to <body> — re-apply font-inter since the CRM's
                  typeface wrapper doesn't reach portals. */}
              <div
                className={`fixed ${Z.modal} w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 font-inter shadow-lg dark:border-gray-700 dark:bg-gray-800`}
                style={{ top: menuPos.top, left: menuPos.left }}
              >
                <p className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Set status</p>
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatus(f, opt.value)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <span className="flex items-center gap-2">
                      {opt.value === 'archived' && <Archive className="h-4 w-4" />}
                      {opt.label}
                    </span>
                    {f.status === opt.value && <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
                  </button>
                ))}
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                <button
                  type="button"
                  onClick={() => duplicate(f)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <Copy className="h-4 w-4" /> Duplicate
                </button>
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                <button
                  type="button"
                  onClick={() => deleteForm(f)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 transition-colors duration-150 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </>,
            document.body,
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-5xl">
      <PageHeader
        icon={ClipboardList}
        title="Forms"
        subtitle={<>Mobile forms recruiters text to patients — built here, filled at /f/&lt;link&gt;.</>}
        actions={
          canEdit && (
            <>
              <button
                onClick={openImport}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
              >
                <Download className="h-4 w-4" /> Import from WCR
              </button>
              <button
                onClick={() => setNewOpen(true)}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
              >
                <Plus className="h-4 w-4" /> New form
              </button>
            </>
          )
        }
      />

      {notice && (
        <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {notice}
        </div>
      )}
      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Inline create */}
      {newOpen && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
              placeholder="Form title (e.g. Comprehensive health intake)"
              className="min-w-[12rem] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            <select
              value={newSurface}
              onChange={(e) => setNewSurface(e.target.value)}
              aria-label="Form group"
              className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {FORM_SURFACES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              onClick={create}
              disabled={creating || !newTitle.trim()}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
            <button onClick={() => setNewOpen(false)} className="rounded p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* WCR import panel */}
      {importOpen && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Import a WCR questionnaire</h3>
            <button onClick={() => setImportOpen(false)} className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Questions keep their original ids, so a patient's old web answers and new form answers line up automatically.
          </p>
          {wcrLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : wcrAvailable && wcrList.length > 0 ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {wcrList.map((q) => (
                <div key={q.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{q.name}</p>
                    <p className="text-xs text-gray-400">
                      {q.question_count} questions{q.study_title ? ` · ${q.study_title}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => importById(q)}
                    disabled={!!importing}
                    className="shrink-0 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                  >
                    {importing === q.id ? 'Importing…' : 'Import'}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">
                {wcrAvailable === false
                  ? 'The WCR questionnaire list is not reachable from here — paste the questionnaire JSON instead (WCR admin → questionnaire → export).'
                  : 'No active questionnaires found — you can still paste one as JSON.'}
              </p>
              <textarea
                rows={5}
                value={pasteJson}
                onChange={(e) => setPasteJson(e.target.value)}
                placeholder='{"id": "…", "name": "…", "questions": [...]}'
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
              <button
                onClick={importPasted}
                disabled={!!importing || !pasteJson.trim()}
                className="mt-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {importing === 'paste' ? 'Importing…' : 'Import JSON'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Forms list */}
      <div className="mt-5">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : forms.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No forms yet. {canEdit ? 'Create one or import a WCR questionnaire to get started.' : 'A CRM admin can create the first one.'}
            </p>
          </div>
        ) : (
          <>
            <SegmentedTabs
              label="Form status"
              multiple
              tabs={statusTabs}
              values={filters}
              onChange={setFilters}
              className="mb-4"
            />

            <FilterBar className="mb-4">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search forms by name…"
                label="Search forms"
                className="flex-1 basis-56"
              />
            </FilterBar>

            {visibleForms.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                {q
                  ? `No forms match "${search.trim()}".`
                  : `No ${filters.length === 1 ? `${FILTERS.find((x) => x.value === filters[0])?.label.toLowerCase() ?? ''} ` : filters.length > 1 ? 'matching ' : ''}forms to show.`}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Item #05: two groups — Landing Page Forms vs Study Questionnaires. */}
                {FORM_SURFACES.map((group) => {
                  const groupForms = visibleForms.filter((f) => formSurface(f.surface) === group.value);
                  if (groupForms.length === 0) return null;
                  return (
                    <div key={group.value}>
                      <h2 className="mb-2 px-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {group.plural} <span className="font-normal text-gray-400">{groupForms.length}</span>
                      </h2>
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {groupForms.map(renderRow)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
